import React, { FC, useMemo, useEffect, useState, useRef } from 'react';
import { HiArrowPath, HiPencil, HiTableCells, HiTrash } from 'react-icons/hi2';
import { t } from 'i18next';
import audioPlaceholder from '../../../assets/images/audio-file.png';
import documentPlaceholder from '../../../assets/images/pdf.png';

interface FilePreviewProps {
  file: File | null;
  onRemove: () => void;
  onReplace: () => void;
  onNameChange?: (name: string) => void;
  className?: string;
}

const FilePreview: FC<FilePreviewProps> = ({
  file,
  onRemove,
  onReplace,
  onNameChange,
  className = '',
}) => {
  const [basename, setBasename] = useState('');
  const [extension, setExtension] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePencilClick = () => {
    inputRef.current?.focus();
  };

  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return null;
  }, [file]);

  useEffect(() => {
    if (file) {
      const lastDotIndex = file.name.lastIndexOf('.');
      if (lastDotIndex > 0) {
        setBasename(file.name.substring(0, lastDotIndex));
        setExtension(file.name.substring(lastDotIndex));
      } else {
        setBasename(file.name);
        setExtension('');
      }
    }
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleBasenameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBasename = e.target.value;
    setBasename(newBasename);
    onNameChange?.(newBasename + extension);
  };

  const renderPreview = () => {
    if (!file || !previewUrl) return null;

    if (file.type.startsWith('image/')) {
      return (
        <img
          src={previewUrl}
          alt={basename + extension}
          className="max-h-full max-w-full object-contain"
        />
      );
    }

    if (file.type.startsWith('video/')) {
      return (
        <video
          src={previewUrl}
          className="max-h-full max-w-full object-contain"
          controls
          muted
        />
      );
    }

    if (file.type.startsWith('audio/')) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4">
          <img
            src={audioPlaceholder}
            alt={basename + extension}
            className="h-24 w-24 object-contain"
          />
          <audio src={previewUrl} controls className="w-full max-w-xs" />
        </div>
      );
    }

    if (file.type === 'application/pdf') {
      return (
        <img
          src={documentPlaceholder}
          alt={basename + extension}
          className="h-24 w-24 object-contain"
        />
      );
    }

    // Excel files
    if (
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel'
    ) {
      return (
        <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
          <HiTableCells className="h-12 w-12 text-green-600 dark:text-green-400" />
        </div>
      );
    }

    return null;
  };

  if (!file || !previewUrl) return null;

  return (
    <div className={className}>
      {/* Preview container */}
      <div className="relative overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
        {/* Preview content */}
        <div className="flex h-64 items-center justify-center p-4 pb-14">
          {renderPreview()}
        </div>

        {/* Filename - bottom overlay */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-3 pt-6 dark:from-black/80">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePencilClick}
              className="shrink-0 rounded p-0.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white dark:text-white/80 dark:hover:bg-white/20 dark:hover:text-white"
            >
              <HiPencil className="h-4 w-4" />
            </button>
            <input
              ref={inputRef}
              type="text"
              value={basename}
              onChange={handleBasenameChange}
              className="min-w-0 flex-1 truncate border-transparent bg-transparent px-1 py-0.5 text-sm font-medium text-white transition-colors placeholder:text-gray-300 focus:border-white/30 focus:bg-white/10 focus:ring-0 dark:focus:border-white/40 dark:focus:bg-white/20"
            />
            <span className="shrink-0 text-sm font-medium text-white/70">
              {extension}
            </span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-3 flex gap-2">
        <button
          onClick={onReplace}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          <HiArrowPath className="h-4 w-4" />
          {t('media.replace')}
        </button>
        <button
          onClick={onRemove}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-500 dark:text-red-300 dark:hover:bg-red-900/30"
        >
          <HiTrash className="h-4 w-4" />
          {t('media.delete')}
        </button>
      </div>
    </div>
  );
};

export default FilePreview;
